import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RecommendationForm = ({ caseId, recommendation, caseInfo, agent, onUpdate }) => {
  const [data, setData] = useState(recommendation || {});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!recommendation) {
      setData({});
    } else {
      setData(recommendation);
    }
  }, [recommendation]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/recommendation?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate();
    } finally { setSaving(false); }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/ai-recommendation?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generation failed');
      }
      const { fields } = await res.json();
      // Backend now persists to MongoDB automatically; refresh local state with merged values
      const merged = { ...data, ...fields };
      setData(merged);
      // Belt-and-suspenders: also POST the merged record so any edits the advisor made before
      // clicking AI are kept (backend already saved AI fields, this re-saves the union).
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/recommendation?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(merged)
      });
      onUpdate && onUpdate();
      alert('AI draft generated and saved. Edits below will require Save again.');
    } catch (e) {
      alert(`AI generation failed: ${e.message}`);
    } finally { setGenerating(false); }
  };

  return (
    <Card data-testid="recommendation-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle>Recommendation Summary</CardTitle>
        <div className="flex gap-2">
          <Button onClick={generateWithAI} disabled={generating} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-recommendation-btn">
            <Sparkles className="h-4 w-4 mr-2" />{generating ? 'Generating...' : 'Generate with AI'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-recommendation-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Array.isArray(data.proposed_products) && data.proposed_products.length > 0 && (
          <div className="border rounded-lg overflow-hidden" data-testid="proposed-products-table">
            <div className="bg-[#003D5C] text-white px-4 py-2 flex items-center justify-between">
              <span className="font-semibold text-sm">Proposed Product Mix (AI-suggested)</span>
              <span className="text-xs opacity-80">{data.proposed_products.length} item{data.proposed_products.length === 1 ? '' : 's'}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-3 py-2">Insured</th>
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Face / Benefit</th>
                    <th className="px-3 py-2">Term</th>
                    <th className="px-3 py-2">Est. Premium</th>
                    <th className="px-3 py-2">Riders</th>
                    <th className="px-3 py-2">Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.proposed_products.map((p, i) => {
                    const face = Number(p.face_amount || 0);
                    const monthly = Number(p.monthly_benefit || 0);
                    const faceCol = monthly && !face
                      ? `$${monthly.toLocaleString()}/mo`
                      : (face ? `$${face.toLocaleString()}${monthly ? ` + $${monthly.toLocaleString()}/mo` : ''}` : '—');
                    const riders = Array.isArray(p.riders) ? p.riders.join(', ') : (p.riders || '—');
                    const prem = Number(p.estimated_monthly_premium || 0);
                    return (
                      <tr key={p.id || `${p.insured || 'p'}-${p.product_type || 't'}-${i}`} className="border-t align-top" data-testid={`proposed-product-row-${i}`}>
                        <td className="px-3 py-2 font-medium">{p.insured || '—'}</td>
                        <td className="px-3 py-2">{p.product_type || '—'}</td>
                        <td className="px-3 py-2">{faceCol}</td>
                        <td className="px-3 py-2">{p.term_or_duration || '—'}</td>
                        <td className="px-3 py-2">{prem ? `$${prem.toLocaleString()}/mo` : '—'}</td>
                        <td className="px-3 py-2 text-xs">{riders || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{p.rationale || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t">
              The AI proposed this layered mix from the Fact-Find + Needs Analysis. Review each row,
              edit the headline fields below if needed, then click <b>Save</b>. The full mix is preserved
              in the final compliance PDF.
            </p>
          </div>
        )}
        {[
          { key: 'client_objective', label: "Client's Objective", type: 'textarea' },
          { key: 'summary_of_needs', label: 'Summary of Needs', type: 'textarea' },
          { key: 'suitability_reason', label: 'Suitability Reason', type: 'textarea' },
          { key: 'alternatives_considered', label: 'Alternatives Considered', type: 'textarea' },
          { key: 'why_alternatives_not_selected', label: 'Why Alternatives Not Selected', type: 'textarea' },
          { key: 'risks_limitations_discussed', label: 'Risks & Limitations Discussed', type: 'textarea' },
        ].map(f => (
          <div key={f.key}>
            <label className="block text-sm font-medium mb-1">{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea rows="2" value={data[f.key] || ''} onChange={(e) => setData({ ...data, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid={`rec-${f.key}`} />
            ) : (
              <input type={f.type} value={data[f.key] || ''} onChange={(e) => setData({ ...data, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid={`rec-${f.key}`} />
            )}
          </div>
        ))}
        <div className="flex gap-6 pt-3 border-t">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.affordability_confirmed || false} onChange={(e) => setData({ ...data, affordability_confirmed: e.target.checked })} data-testid="rec-affordability" />
            <span className="text-sm">Affordability Confirmed</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.advisor_certification || false} onChange={(e) => setData({ ...data, advisor_certification: e.target.checked })} data-testid="rec-advisor-cert" />
            <span className="text-sm">Advisor Certifies Recommendation</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_acknowledgement || false} onChange={(e) => setData({ ...data, client_acknowledgement: e.target.checked })} data-testid="rec-client-ack" />
            <span className="text-sm">Client Acknowledged</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================
// Replacement Form (LIRD + Comparative)
// =============================================================
